import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw, FileSpreadsheet, PlusCircle, MinusCircle, PencilLine, User } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const timeAgo = (iso) => {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleString();
};

function ChangeLine({ ch }) {
  if (ch.type === "row_added")
    return <li className="flex items-start gap-2 text-emerald-700"><PlusCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>Row {ch.row} added <span className="text-slate-500">({ch.row_label})</span></span></li>;
  if (ch.type === "row_deleted")
    return <li className="flex items-start gap-2 text-red-600"><MinusCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>Row {ch.row} deleted <span className="text-slate-500">({ch.row_label})</span></span></li>;
  return (
    <li className="flex items-start gap-2 text-slate-600">
      <PencilLine className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
      <span>
        <span className="font-medium text-slate-700">R{ch.row}</span> <span className="text-slate-400">({ch.row_label})</span> · <span className="font-medium text-slate-700">C{ch.col}</span> <span className="text-slate-400">({ch.col_label})</span>:{" "}
        <span className="font-mono text-xs px-1 rounded bg-red-50 text-red-600 line-through">{ch.old || "—"}</span>
        <span className="mx-1">→</span>
        <span className="font-mono text-xs px-1 rounded bg-emerald-50 text-emerald-700">{ch.new || "—"}</span>
      </span>
    </li>
  );
}

export function MISActivityFeed() {
  const [sheet, setSheet] = useState("all");
  const { data: sheets } = useQuery({ queryKey: ["edit-sheets"], queryFn: async () => (await api.get("/sales-mis/edit-sheets")).data });
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["mis-activity", sheet],
    queryFn: async () => (await api.get("/sales-mis/activity", { params: sheet === "all" ? {} : { sheet } })).data,
    refetchInterval: 60000,
  });
  const items = data || [];
  const options = [{ slug: "board", title: "Sales Dash Board" }, ...(sheets || [])];

  return (
    <div data-testid="mis-activity-feed">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-sm font-semibold text-slate-800">Change activity</p>
          <p className="text-xs text-slate-400">Every Sales MIS edit — who changed what, and the exact row & column.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sheet} onValueChange={setSheet}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="activity-sheet-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sheets</SelectItem>
              {options.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => refetch()} className="gap-1.5" data-testid="activity-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <Card className="py-16 text-center text-sm text-slate-400" data-testid="activity-empty">No changes recorded yet.</Card>
      ) : (
        <div className="space-y-3">
          {items.map((a, i) => (
            <Card key={a.id} className="p-4" data-testid={`activity-item-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-7 w-7 rounded-full bg-[#FFEAE7] text-brand flex items-center justify-center shrink-0"><User className="h-3.5 w-3.5" /></span>
                  <span className="text-sm font-medium text-slate-800 truncate">{a.editor_name}</span>
                  <Badge variant="secondary" className="gap-1 font-normal"><FileSpreadsheet className="h-3 w-3" />{a.sheet}</Badge>
                  <span className="text-xs text-slate-400">· {a.count} change{a.count === 1 ? "" : "s"}</span>
                </div>
                <span className="text-xs text-slate-400 shrink-0">{timeAgo(a.created_at)}</span>
              </div>
              <ul className="space-y-1 text-sm pl-1">
                {(a.changes || []).slice(0, 25).map((ch, k) => <ChangeLine key={k} ch={ch} />)}
                {(a.changes || []).length > 25 && <li className="text-xs text-slate-400 pl-5">…and {a.changes.length - 25} more change(s)</li>}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
